import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { UserContext } from 'contexts/ConfigContext';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import React, { FC, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { queryClient } from 'services/reactQueryService';
import { getAllPlansVariablesType } from 'types/tradePromoOptimization';
import { getAuthUser, getConfig } from 'utils/helperMethods';
import { CustomTable } from '../PricingSimulator/PricingSimulatorIndexTableWrapper';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { useFetchAllPlans } from 'hooks/tradePromoOptimization/plans/useFetchAllPlans';
import { tpoReactQueryKeyConstants } from 'utils/Enums';
import { tradePromoOptimizationUrls } from 'config/urls';
import { isNil } from 'lodash';
import { tpoPlansTableColumns } from 'utils/getTradePromoOptimizationTableColumns';
import { TPODataContext } from 'contexts/TPOContext';
import TableErrorBox from 'components/molecules/ErrorBox/TableErrorBox';
import { CreatePlanModalType, DeletePlanModalType } from 'components/organism/TradePromoOptimization/Plans';

const plansTableId = 'plans-index-table';

type Props = {
    tableReactQueryVariables: getAllPlansVariablesType;
    setTableReactQueryVariables: Function;
    setCreateEditPlanModal: React.Dispatch<React.SetStateAction<CreatePlanModalType>>;
    setDeletePlanModal: React.Dispatch<React.SetStateAction<DeletePlanModalType>>;
};

const PlansIndexTableWrapper: FC<Props> = ({
    tableReactQueryVariables,
    setTableReactQueryVariables,
    setCreateEditPlanModal,
    setDeletePlanModal,
}) => {
    const { appConfig } = useContext(UserContext);
    const { showSyncedPlans } = useContext(TPODataContext);
    const tradePromoOptimizationConfig = getConfig('tradePromoOptimization', appConfig);
    const screenDetailsWithRoutes = getConfig('screenDetailsWithRoutes', appConfig);
    const indexTableFixedColumns = tradePromoOptimizationConfig.plansIndexTableFixedColumns;
    const statuses = tradePromoOptimizationConfig.statuses;
    const location = useLocation();

    const { dimensionConfigData, configDataExists, kpiConfigLoading } = useContext(TPODataContext);

    const mainSectionHeight = useRef<number | null>(0);

    //this hook with give height to fill the screen height
    mainSectionHeight.current = useFillHeightToFitScreen({
        id: plansTableId,
        height: mainSectionHeight.current,
        buffer: 80,
        // triggerDummyRender: true,
    });

    //remove all plans from cache when user leaves index page so that on revisit fresh query is called to get latest data
    useEffect(() => {
        return () => {
            queryClient.removeQueries({
                exact: false,
                queryKey: [tpoReactQueryKeyConstants.GET_ALL_PLANS],
            });
        };
    }, []);

    //get all plans
    const {
        data: allPlansData,
        loading: allPlansDataLoading,
        error: allPlansDataError,
        count: allPlansDataCount,
        setData: setAllProjectsData,
    } = useFetchAllPlans({
        method: 'post',
        queryConfig: {
            enabled:
                configDataExists &&
                Boolean(tableReactQueryVariables && Object.keys(tableReactQueryVariables?.filters)?.length > 0),
        },
        queryKey: [tpoReactQueryKeyConstants.GET_ALL_PLANS, tableReactQueryVariables],
        url: `${tradePromoOptimizationUrls.getAllPlansUrl}`,
        variables: {
            ...tableReactQueryVariables,
        },
    });
    const commonLoading = kpiConfigLoading || allPlansDataLoading;

    function editClickHandler(rowData: any) {
        setCreateEditPlanModal({ data: rowData, open: true, isEdit: true });
    }

    function deleteClickHandler(rowData: any) {
        setDeletePlanModal({ data: rowData, open: true });
    }

    //table columns
    const memoisedColumns = useMemo(() => {
        if (!allPlansData) return [];
        const tpoIndexPage = screenDetailsWithRoutes?.find((item: any) => item?.id === 'tpo');
        const plansIndexPageRoute = tpoIndexPage?.subRoutes?.find((subRoutes: any) => {
            return subRoutes?.pageId === 'plan';
        });
        const basePlanUrl = `/${tpoIndexPage?.path}/${plansIndexPageRoute?.suffix}`;
        let attributeTypeColumns = [dimensionConfigData?.dim1Attribute, dimensionConfigData?.dim2Attribute];
        attributeTypeColumns = attributeTypeColumns.filter((item) => !isNil(item));
        return tpoPlansTableColumns(
            indexTableFixedColumns,
            attributeTypeColumns,
            statuses,
            basePlanUrl,
            {
                sortBy: tableReactQueryVariables?.sort_by || 0,
                sortOrder:
                    tableReactQueryVariables?.sorting_order === 'asc'
                        ? 'ascend'
                        : tableReactQueryVariables?.sorting_order === 'desc'
                        ? 'descend'
                        : null,
            },
            editClickHandler,
            showSyncedPlans,
            deleteClickHandler,
        );
    }, [allPlansData]);

    function infiniteScrollOnNext(loading: boolean) {
        if (!loading) {
            const newOffset = tableReactQueryVariables.offset + tableReactQueryVariables.limit;
            setTableReactQueryVariables({ ...tableReactQueryVariables, offset: newOffset });
        }
    }

    return (
        <div>
            <InfiniteScroller
                data={allPlansData}
                loading={commonLoading}
                hasMore={allPlansData?.length < allPlansDataCount || commonLoading}
                height={
                    allPlansData && !allPlansDataError
                        ? mainSectionHeight.current ?? undefined
                        : isNaN(Number(mainSectionHeight.current))
                        ? undefined
                        : Number(mainSectionHeight.current) - 1 //there should be a change in height when its loading and not loading, thats the reason for subtracting 1
                }
                //height={200}
                //     loader={true}
                onNext={() => infiniteScrollOnNext(commonLoading)}
            >
                <CustomTable
                    onChange={(pagination, filter, sort: any, extra) => {
                        if (sort?.order) {
                            const sortMapping: any = {
                                descend: 'desc',
                                ascend: 'asc',
                            };

                            setTableReactQueryVariables({
                                ...tableReactQueryVariables,
                                sort_by: sort?.column?.sortKey,
                                sorting_order: sortMapping?.[sort?.order] || 'desc',
                                offset: 0,
                            });
                            //default it to dim1 column on cancelling sorting
                        } else {
                            setTableReactQueryVariables({
                                ...tableReactQueryVariables,
                                sort_by: 'updated_at',
                                sorting_order: 'desc',
                                offset: 0,
                            });
                        }
                    }}
                    sticky={{
                        getContainer: () =>
                            document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement,
                    }} //adding x axis scrollbar on table
                    id={plansTableId}
                    //loading={commonLoading}
                    columns={memoisedColumns}
                    scroll={{ x: '100%' }}
                    dataSource={allPlansData}
                    pagination={false}
                    style={{
                        opacity: commonLoading ? '0.3' : 1,
                        pointerEvents: commonLoading ? 'none' : 'auto',
                    }}
                    locale={{
                        emptyText: commonLoading ? (
                            <></>
                        ) : allPlansDataError ? (
                            <TableErrorBox error={allPlansDataError} height={mainSectionHeight.current} />
                        ) : (
                            ''
                        ),
                    }}
                    rowClassName={(record: any) => {
                        let className = '';

                        //if expired, then disabled row
                        if (record?.status === 'expired') {
                            className += 'opacity-60 cursor-not-allowed pointer-events-none';
                        }
                        return className;
                    }}
                />
            </InfiniteScroller>
        </div>
    );
};

export default PlansIndexTableWrapper;
